import { useCallback, useEffect, useState } from 'react'
import { getDataCenter, getSyncJob, syncBilibili } from '../api/data'
import { ApiError } from '../api/client'
import { PageHeader, StatusMessage } from '../components/AppShell'
import type { DataCenterData, LoadState } from '../types/api'

type JsonRecord = Record<string, unknown>
type FieldCatalog = Record<string, FieldDefinition>
type FieldDefinition = { label?: string; meaning?: string; children?: FieldCatalog; dynamic?: FieldDefinition }

function asRecord(value: unknown): JsonRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {}
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function findValue(value: unknown, names: string[]): unknown {
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findValue(item, names)
      if (found !== undefined) return found
    }
    return undefined
  }
  if (value === null || typeof value !== 'object') return undefined
  const record = value as JsonRecord
  for (const name of names) if (name in record) return record[name]
  for (const child of Object.values(record)) {
    const found = findValue(child, names)
    if (found !== undefined) return found
  }
  return undefined
}

function numberValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value)
  return undefined
}

function formatMetric(value: number | undefined) {
  if (value === undefined) return '—'
  if (Math.abs(value) >= 10000) return `${(value / 10000).toFixed(value >= 100000 ? 0 : 1)}万`
  return new Intl.NumberFormat('zh-CN').format(value)
}

function formatDate(value: unknown) {
  if (!value) return '暂无时间'
  const raw = String(value)
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? raw : new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit' }).format(date)
}

function displayValue(value: unknown) {
  if (value === null) return 'null'
  if (value === undefined) return '未提供'
  if (typeof value === 'object') {
    const size = Array.isArray(value) ? value.length : Object.keys(value as JsonRecord).length
    return `${Array.isArray(value) ? '数组' : '对象'} · ${size}${Array.isArray(value) ? ' 项' : ' 个字段'}`
  }
  return String(value)
}

function lookupField(catalog: FieldCatalog, path: string[]) {
  let current: FieldDefinition | undefined
  for (const part of path) {
    if (/^\d+$/.test(part)) continue
    if (!current) current = catalog[part]
    else current = current.children?.[part] || current.dynamic
    if (!current) break
  }
  return current || {}
}

function FieldTree({ name, value, catalog, path = [], level = 0 }: { name: string; value: unknown; catalog: FieldCatalog; path?: string[]; level?: number }) {
  const definition = lookupField(catalog, path)
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value)
      ? value.map((item, index) => [String(index), item] as const)
      : Object.entries(value as JsonRecord)
    return <details className="data-center-field-node" style={{ marginLeft: level * 10 }}>
      <summary><b>{name}</b><span>{definition.label || '上游字段'} · {Array.isArray(value) ? `数组 · ${entries.length} 项` : `对象 · ${entries.length} 个字段`}</span></summary>
      {definition.meaning ? <p className="data-center-field-meaning">{definition.meaning}</p> : null}
      <div>{entries.length ? entries.map(([key, item]) => <FieldTree key={`${name}-${key}`} name={key} value={item} catalog={catalog} path={[...path, key]} level={level + 1} />) : <em>上游返回空对象</em>}</div>
    </details>
  }
  return <div className="data-center-field-value" style={{ marginLeft: level * 10 }}><div><b>{name}</b><span>{definition.label || '上游字段'}</span><small>{definition.meaning || '按上游原字段展示，暂无额外协议说明。'}</small></div><strong>{displayValue(value)}</strong></div>
}

function MetricGroup({ group }: { group: JsonRecord }) {
  const items = asArray(group.items).map(asRecord)
  return <details open className="data-center-catalog-group"><summary className="data-center-catalog-group-summary"><div><span className="eyebrow">{String(group.key || 'ACCOUNT DATA').toUpperCase()}</span><h3>{String(group.label || '账号数据')}</h3></div><span>{items.length} 组接口快照</span></summary><div className="data-center-catalog-items">{items.map((item, index) => {
    const hasData = Object.prototype.hasOwnProperty.call(item, 'data')
    const fieldCatalog = asRecord(item.field_catalog) as FieldCatalog
    return <details className="data-center-catalog-item" key={`${String(item.metric_type || index)}-${String(item.captured_at || index)}`}>
      <summary><div><strong>{String(item.label || item.metric_type || `接口 ${index + 1}`)}</strong><small>{String(item.metric_type || '')} · {String(item.endpoint || '接口地址未提供')}</small></div><span>{formatDate(item.captured_at)}</span></summary>
      <div className="data-center-catalog-item-body">
        {hasData ? <FieldTree name="data" value={item.data} catalog={fieldCatalog} /> : <div className="data-center-data-missing">上游响应未返回 <code>data</code>，没有可展示的字段。</div>}
      </div>
    </details>
  })}</div></details>
}

function RecordCatalog({ title, items, catalog, getTitle, getValue }: { title: string; items: unknown[]; catalog: FieldCatalog; getTitle?: (item: JsonRecord) => string; getValue?: (item: JsonRecord) => unknown }) {
  return <details open className="data-center-catalog-group"><summary className="data-center-catalog-group-summary"><div><span className="eyebrow">FIELD CATALOG</span><h3>{title}</h3></div><span>{items.length} 条记录</span></summary><div className="data-center-catalog-items">{items.map((value, index) => {
    const item = asRecord(value)
    return <details className="data-center-catalog-item" key={`${String(item.bvid || item.rpid || item.cid || item.source || index)}-${index}`}>
      <summary><div><strong>{getTitle?.(item) || `记录 ${String(index + 1).padStart(2, '0')}`}</strong><small>展开查看上游字段及含义</small></div><span>{formatDate(item.captured_at)}</span></summary>
      <div className="data-center-catalog-item-body"><FieldTree name={getValue ? 'data' : 'record'} value={getValue ? getValue(item) : item} catalog={catalog} /></div>
    </details>
  })}</div></details>
}

const KPI_FIELDS = [
  ['total_fans', '累计粉丝', 'ri-user-follow-line'],
  ['total_click', '累计播放', 'ri-play-circle-line'],
  ['total_like', '累计点赞', 'ri-thumb-up-line'],
  ['total_reply', '累计评论', 'ri-chat-3-line'],
] as const

export function DataCenterPage() {
  const [state, setState] = useState<LoadState>('loading')
  const [data, setData] = useState<DataCenterData>()
  const [syncing, setSyncing] = useState(false)
  const [syncNotice, setSyncNotice] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    try {
      const result = await getDataCenter()
        setData(result || {})
        setState('ready')
    } catch (error) {
      setState(error instanceof ApiError && error.unavailable ? 'unavailable' : 'error')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const sync = useCallback(async () => {
    if (syncing) return
    setSyncNotice('')
    setSyncing(true)
    try {
      const submitted = await syncBilibili()
      setSyncNotice('正在读取 B 站账号内容与指标…')
      for (let attempt = 0; attempt < 120; attempt += 1) {
        const job = await getSyncJob(submitted.job_id)
        if (job.status === 'completed' || job.status === 'partial') {
          setSyncNotice(job.status === 'partial' ? String((job.result as { warning?: string } | undefined)?.warning || '账号数据已部分读取，请检查缺失字段。') : '账号数据读取完成。')
          await load()
          return
        }
        if (job.status === 'needs_login') throw new Error('B 站登录态已失效，请重新扫码登录')
        if (['error', 'interrupted', 'unavailable'].includes(job.status)) throw new Error(job.error || 'B 站数据读取失败')
        await new Promise(resolve => window.setTimeout(resolve, 1000))
      }
      throw new Error('读取超时，请稍后重试')
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setSyncNotice('请先扫码登录 B 站账号，再重新读取数据。')
      else setSyncNotice(error instanceof Error ? error.message : 'B 站数据读取失败')
    } finally {
      setSyncing(false)
    }
  }, [load, syncing])

  const account = asRecord(data?.account)
  const accountGroups = asArray(account.groups).map(asRecord)
  const accountItems = accountGroups.flatMap(group => asArray(group.items))
  const contentSection = asRecord(data?.content)
  const content = asArray(contentSection.items)
  const feedback = asRecord(data?.feedback)
  const commentsSection = asRecord(feedback.comments)
  const danmakuSection = asRecord(feedback.danmaku)
  const comments = asArray(commentsSection.items)
  const danmaku = asArray(danmakuSection.items)
  const kpis = KPI_FIELDS.map(([key, label, icon]) => ({ key, label, icon, value: numberValue(findValue(accountItems, [key])) }))
  const capturedAt = data?.captured_at

  return <>
    <PageHeader eyebrow="CONTENT ANALYTICS" title="数据中心" action={<button className="btn btn-primary data-center-sync-button" onClick={() => void sync()} disabled={state === 'loading' || syncing}><i className={syncing ? 'ri-loader-4-line eval-spin' : 'ri-refresh-line'} aria-hidden="true" /> {syncing ? '同步中…' : '同步账号数据'}</button>} />
    {syncNotice ? <div className="inline-notice data-center-sync-notice" role={syncing ? 'status' : 'alert'}>{syncNotice}</div> : null}
    {state !== 'ready' ? <StatusMessage state={state === 'idle' ? 'loading' : state} onRetry={load} /> : <>
      <section className="data-center-summary" aria-label="数据中心概览">
        <section className="data-center-beta-banner" aria-label="数据中心 Beta 版本提示"><span className="data-center-beta-badge">BETA</span><div><strong>数据中心 Beta 版</strong><p>当前页面仍在持续完善中，数据展示与分析能力会逐步更新。</p></div><i className="ri-flask-line" aria-hidden="true" /></section>
        <div className="card data-center-summary-main"><div className="data-center-summary-icon"><i className="ri-bar-chart-box-line" aria-hidden="true" /></div><div><span className="eyebrow">ACCOUNT SNAPSHOT</span><h2>账号数据已就绪</h2><p>按接口职责分类查看同步到的账号、内容与反馈数据。</p></div><div className="data-center-captured"><span>最近抓取</span><strong>{formatDate(capturedAt)}</strong></div></div>
        <div className="data-center-overview-grid"><div className="card data-center-overview-card"><span>已收录视频</span><strong>{content.length}</strong><small>条内容数据</small></div><div className="card data-center-overview-card"><span>接口快照</span><strong>{accountItems.length}</strong><small>组账号数据</small></div><div className="card data-center-overview-card"><span>互动反馈</span><strong>{comments.length + danmaku.length}</strong><small>条评论与弹幕</small></div></div>
      </section>

      <div className="data-center-layout">
        <section className={`card data-center-content-card ${content.length ? '' : 'is-empty'}`} aria-labelledby="data-center-content-title"><header className="data-center-section-head"><div><span className="eyebrow">CONTENT PERFORMANCE</span><h2 id="data-center-content-title">投稿内容</h2><p>按最近抓取顺序查看视频表现和诊断信息。</p></div><span className="badge badge-blue">{content.length} 条</span></header><div className="data-center-video-list">{content.length ? content.slice(0, 8).map((item, index) => { const video = asRecord(item); const stat = asRecord(video.stat); const view = numberValue(findValue(stat, ['view', 'play'])); const like = numberValue(findValue(stat, ['like'])); const reply = numberValue(findValue(stat, ['reply', 'comment'])); return <article className="data-center-video-row" key={`${String(video.bvid || video.title || index)}-${index}`}><span className="data-center-video-index">{String(index + 1).padStart(2, '0')}</span><div className="data-center-video-copy"><h3>{String(video.title || '未命名投稿')}</h3><p>{formatDate(video.pubdate)} 发布 · {video.bvid ? String(video.bvid) : '暂无编号'}</p><div className="data-center-video-metrics"><span><i className="ri-play-line" aria-hidden="true" />{formatMetric(view)}</span><span><i className="ri-thumb-up-line" aria-hidden="true" />{formatMetric(like)}</span><span><i className="ri-chat-3-line" aria-hidden="true" />{formatMetric(reply)}</span></div></div><i className="ri-arrow-right-s-line data-center-video-arrow" aria-hidden="true" /></article> }) : <div className="data-center-empty"><i className="ri-video-line" aria-hidden="true" /><strong>还没有投稿内容</strong><span>同步账号数据后，这里会展示视频表现。</span></div>}</div></section>

        <section className="card data-center-kpi-card" aria-labelledby="data-center-kpi-title"><header className="data-center-section-head compact"><div><span className="eyebrow">ACCOUNT METRICS</span><h2 id="data-center-kpi-title">账号指标</h2></div><i className="ri-pulse-line data-center-head-icon" aria-hidden="true" /></header><div className="data-center-kpi-grid">{kpis.map(item => <div className="data-center-kpi" key={item.key}><span className="data-center-kpi-icon"><i className={item.icon} aria-hidden="true" /></span><div><small>{item.label}</small><strong>{formatMetric(item.value)}</strong></div></div>)}</div></section>
        <section className={`card data-center-signal-card ${accountGroups.length ? '' : 'is-empty'}`} aria-labelledby="data-center-groups-title"><header className="data-center-section-head compact"><div><span className="eyebrow">DATA GROUPS</span><h2 id="data-center-groups-title">数据分组</h2></div></header><div className="data-center-signal-list">{accountGroups.map(group => <div className="data-center-signal-row" key={String(group.key)}><span className="data-center-signal-icon audience"><i className="ri-database-2-line" aria-hidden="true" /></span><div><strong>{String(group.label || group.key)}</strong><small>{asArray(group.items).length} 组接口快照</small></div><b>已归类</b></div>)}{!accountGroups.length ? <div className="data-center-empty"><span>暂无账号接口数据</span></div> : null}</div></section>
      </div>

      <section className="card data-center-raw-card" aria-labelledby="data-center-raw-title"><header className="data-center-section-head"><div><span className="eyebrow">API FIELD CATALOG</span><h2 id="data-center-raw-title">接口字段</h2><p>按数据来源分类；字段名称保持上游原名，说明仅解释实际返回的字段。</p></div><span className="badge badge-muted">不重复展示</span></header><div className="data-center-catalog">{accountGroups.map(group => <MetricGroup key={String(group.key)} group={group} />)}<RecordCatalog title="投稿内容字段" items={content} catalog={asRecord(contentSection.field_catalog) as FieldCatalog} getTitle={item => String(item.title || '')} /><RecordCatalog title="评论字段" items={comments} catalog={asRecord(commentsSection.field_catalog) as FieldCatalog} /><RecordCatalog title="弹幕字段" items={danmaku} catalog={asRecord(danmakuSection.field_catalog) as FieldCatalog} /></div></section>
    </>}
  </>
}
